/* eslint-disable no-useless-catch */
import axios from 'axios';
import {apiClient} from '@/shared/services/apiClient';
import {logError} from '@/shared/utils/logger';
import {
    AdminOrgSwitchResponse,
    AdminPatientSearchRequest,
    AdminPatientSearchResult,
    AdminLoincCode,
    AdminLoincCodesResponse,
    UnmappedAnalyte,
    AdminSyntheticResultsResponse,
    AdminUserMergeCandidate,
    AdminUserMergePreview,
    AdminUserMergeRequest,
    AdminUserMergeResult,
    UserSearchRequest,
    UserSearchResult,
    Recommendation,
    BiomarkerSearchResult,
    OrganizationSearchResult,
    OrganizationProviderConsoleResponse,
    OrganizationProviderConsoleUpdateRequest,
    GlobalAdminOverviewResponse,
    GlobalAdminRange,
    GlobalAdminTimeSeriesResponse,
    GlobalAdminMetric,
    GlobalAdminOrganizationRow,
    GlobalAdminOrgSort,
    OrganizationCatalogResponse,
    ProviderSearchResult,
    ProviderRenameResponse,
    AddProviderResponse,
    DeleteProviderResponse,
    AssayCatalogItem,
    KitCostSettings,
    KitPackageTier,
    PartnerTier,
    PricingPanel,
    PricingSettings,
    OrgReferenceRange,
    OrgReferenceRangeInput,
    ProviderApiKey,
    ProviderApiKeyCreateResponse,
    StorefrontProduct,
    StorefrontProductInput,
    AdminCoupon,
    AdminCouponListResponse,
    AdminCouponInput,
    BiomarkerDisplayRuleInput,
} from '@/shared/types/api';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';
import settings from '@/settings';

/** Shared response shape of the cross-environment SQL export endpoints. */
export interface SqlExportResult<TCounts> {
    environment: string;
    counts: TCounts;
    emailed: boolean;
    recipients: string[];
    filename: string;
    sql: string;
}

export type BioMatchExportResult = SqlExportResult<{recs: number; rules: number; assays: number}>;
export type GlobalProductsExportResult = SqlExportResult<{products: number; assays: number}>;

/**
 * Admin service for handling admin-specific API calls
 * Following the service layer pattern from best practices guide
 */
export class AdminService {
    /**
     * Search for users in the system
     */
    static async createOrganization(input: {
        name: string;
        subdomain: string;
        ohd?: {organizationId: number; organizationName: string};
    }): Promise<{id: number; name: string; subdomain: string}> {
        return apiClient.post('/api/admin/organizations', input);
    }

    static async searchElisAccounts(query: string): Promise<{
        results: Array<{
            elisAccountId: number;
            name: string;
            mldOrg: {id: number; name: string; subdomain: string} | null;
        }>;
    }> {
        const encoded = encodeURIComponent(query);
        return apiClient.get(`/api/admin/organizations/elis-accounts?q=${encoded}`);
    }

    static async importOrganizationFromElis(input: {
        elisAccountId: number;
        subdomain: string;
    }): Promise<{id: number; name: string; subdomain: string; providersImported: number}> {
        return apiClient.post('/api/admin/organizations/import-from-elis', input);
    }

    static async searchUsers(searchCriteria: UserSearchRequest): Promise<UserSearchResult[]> {
        try {
            return await apiClient.post<UserSearchResult[]>('/api/users', searchCriteria);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Global admin patient search (cross-organization)
     */
    static async searchUsersGlobal(searchCriteria: AdminPatientSearchRequest): Promise<AdminPatientSearchResult[]> {
        try {
            return await apiClient.post<AdminPatientSearchResult[]>('/api/admin/users/search', searchCriteria);
        } catch (error) {
            throw error;
        }
    }

    static async listLoincCodes(params: {
        search?: string;
        needsReview?: boolean;
        limit?: number;
        offset?: number;
        /** 'all' searches the whole published reference (112k+ codes), which is
         *  what identifying a code needs. Omitted means the in-use slice. */
        scope?: 'in-use' | 'all';
        /** Pass false to skip usage counts. Measured on prod: they take a name
         *  search from ~190ms to 400-900ms, and a code lookup has no use for
         *  them. */
        includeUsageCounts?: boolean;
    }): Promise<AdminLoincCodesResponse> {
        const query = new URLSearchParams();
        if (params.search) query.set('search', params.search);
        if (params.needsReview) query.set('needsReview', 'true');
        if (params.limit != null) query.set('limit', String(params.limit));
        if (params.offset != null) query.set('offset', String(params.offset));
        if (params.scope) query.set('scope', params.scope);
        if (params.includeUsageCounts === false) query.set('counts', 'false');
        const qs = query.toString();
        return apiClient.get<AdminLoincCodesResponse>(`/api/admin/loinc-codes${qs ? `?${qs}` : ''}`);
    }

    /** Super-admin-only: includes inactive rules so the review queue is visible. */
    static async listUnmappedAnalytes(): Promise<{rows: UnmappedAnalyte[]}> {
        return apiClient.get<{rows: UnmappedAnalyte[]}>('/api/admin/unmapped-analytes');
    }

    static async listBiomarkerDisplayRules(): Promise<{rules: BiomarkerDisplayRule[]}> {
        return apiClient.get<{rules: BiomarkerDisplayRule[]}>('/api/admin/biomarker-display-rules');
    }

    // create/update return only {id}/{ok} — the controller does not echo the
    // full row back (packages/api/src/modules/biomarker-display-rules/controller.ts).
    // Callers refetch the list on success rather than trusting a returned rule.
    static async createBiomarkerDisplayRule(input: BiomarkerDisplayRuleInput): Promise<{id: number}> {
        return apiClient.post<{id: number}>('/api/admin/biomarker-display-rules', input);
    }

    static async updateBiomarkerDisplayRule(id: number, input: BiomarkerDisplayRuleInput): Promise<{ok: boolean}> {
        return apiClient.put<{ok: boolean}>(`/api/admin/biomarker-display-rules/${id}`, input);
    }

    static async deleteBiomarkerDisplayRule(id: number): Promise<{ok: boolean}> {
        return apiClient.delete<{ok: boolean}>(`/api/admin/biomarker-display-rules/${id}`);
    }

    static async getSyntheticResults(userId: number): Promise<AdminSyntheticResultsResponse> {
        return apiClient.get<AdminSyntheticResultsResponse>(`/api/admin/users/${userId}/synthetic-results`);
    }

    static async createSyntheticOrder(
        userId: number,
        collectedOn: string,
        results: Array<{assayCode: string; value: number; units?: string | null}>
    ): Promise<AdminSyntheticResultsResponse> {
        return apiClient.post<AdminSyntheticResultsResponse>(`/api/admin/users/${userId}/synthetic-results`, {
            collectedOn,
            results,
        });
    }

    static async updateSyntheticResult(
        userId: number,
        orderId: number,
        result: {assayCode: string; value: number; units?: string | null}
    ): Promise<AdminSyntheticResultsResponse> {
        return apiClient.patch<AdminSyntheticResultsResponse>(
            `/api/admin/users/${userId}/synthetic-results/${orderId}`,
            result
        );
    }

    static async deleteSyntheticOrder(userId: number, orderId: number): Promise<AdminSyntheticResultsResponse> {
        return apiClient.delete<AdminSyntheticResultsResponse>(
            `/api/admin/users/${userId}/synthetic-results/${orderId}`
        );
    }

    /**
     * Fetch merge-relevant detail (login/record/activity signals + org links)
     * for the selected user ids — used by the survivor picker.
     */
    static async getMergeCandidates(userIds: number[]): Promise<AdminUserMergeCandidate[]> {
        try {
            return await apiClient.post<AdminUserMergeCandidate[]>('/api/admin/users/merge/candidates', {userIds});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Dry-run a patient merge: returns the per-table impact (rows moved /
     * duplicates dropped) and the survivor/loser detail, without writing.
     */
    static async previewMergeUsers(request: AdminUserMergeRequest): Promise<AdminUserMergePreview> {
        try {
            return await apiClient.post<AdminUserMergePreview>('/api/admin/users/merge/preview', request);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Execute a patient merge. Repoints all of the losers' records onto the
     * survivor and tombstones the loser rows. Irreversible — gate behind an
     * explicit confirmation in the UI.
     */
    static async mergeUsers(request: AdminUserMergeRequest): Promise<AdminUserMergeResult> {
        try {
            return await apiClient.post<AdminUserMergeResult>('/api/admin/users/merge', request);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search organizations (admin action)
     */
    static async searchOrganizations(query: string): Promise<OrganizationSearchResult[]> {
        try {
            const encoded = encodeURIComponent(query.trim());
            return await apiClient.get<OrganizationSearchResult[]>(`/api/admin/organizations/search?q=${encoded}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * List organizations for admin navigation
     */
    static async listOrganizations(limit?: number): Promise<OrganizationSearchResult[]> {
        try {
            const params = typeof limit === 'number' ? `?limit=${encodeURIComponent(String(limit))}` : '';
            return await apiClient.get<OrganizationSearchResult[]>(`/api/admin/organizations${params}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search providers (admin action)
     */
    static async searchProviders(query: string): Promise<ProviderSearchResult[]> {
        try {
            const encoded = encodeURIComponent(query.trim());
            return await apiClient.get<ProviderSearchResult[]>(`/api/admin/providers/search?q=${encoded}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Global admin overview metrics (aggregate-only)
     */
    static async getGlobalOverview(range: GlobalAdminRange): Promise<GlobalAdminOverviewResponse> {
        try {
            const encoded = encodeURIComponent(range);
            return await apiClient.get<GlobalAdminOverviewResponse>(`/api/admin/global/overview?range=${encoded}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Global admin time series (single metric)
     */
    static async getGlobalTimeSeries(
        range: GlobalAdminRange,
        metric: GlobalAdminMetric
    ): Promise<GlobalAdminTimeSeriesResponse> {
        try {
            const encodedRange = encodeURIComponent(range);
            const encodedMetric = encodeURIComponent(metric);
            return await apiClient.get<GlobalAdminTimeSeriesResponse>(
                `/api/admin/global/timeseries?range=${encodedRange}&metric=${encodedMetric}`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Global admin top organizations
     */
    static async getGlobalOrganizations(
        range: GlobalAdminRange,
        sort: GlobalAdminOrgSort,
        limit = 20
    ): Promise<GlobalAdminOrganizationRow[]> {
        try {
            const encodedRange = encodeURIComponent(range);
            const encodedSort = encodeURIComponent(sort);
            const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.min(Math.trunc(limit), 100)) : 20;
            return await apiClient.get<GlobalAdminOrganizationRow[]>(
                `/api/admin/global/orgs?range=${encodedRange}&sort=${encodedSort}&limit=${safeLimit}`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create an org switch token for super-admin navigation
     */
    static async createOrgSwitchToken(organizationId: number): Promise<{token: string}> {
        try {
            return await apiClient.post<{token: string}>('/api/admin/org-switch', {organization_id: organizationId});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create an org switch token using a specific bearer token (used for impersonation return).
     */
    static async createOrgSwitchTokenWithToken(organizationId: number, token: string): Promise<{token: string}> {
        try {
            const response = await axios.post(
                `${settings.apiURL}/api/admin/org-switch`,
                {organization_id: organizationId},
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Exchange org switch token for an admin access token
     */
    static async exchangeOrgSwitchToken(token: string): Promise<AdminOrgSwitchResponse> {
        try {
            return await apiClient.post<AdminOrgSwitchResponse>('/api/admin/org-switch/exchange', {token});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create a provider console switch token for organization users
     */
    static async createProviderSwitchToken(
        providerId: number,
        organizationId?: number | null
    ): Promise<{token: string; return_token: string}> {
        try {
            return await apiClient.post<{token: string; return_token: string}>('/api/org/provider-switch', {
                provider_id: providerId,
                organization_id: organizationId ?? undefined,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Exchange provider switch token for a provider-scoped access token
     */
    static async exchangeProviderSwitchToken(token: string): Promise<AdminOrgSwitchResponse> {
        try {
            return await apiClient.post<AdminOrgSwitchResponse>('/api/org/provider-switch/exchange', {token});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Exchange org console return token for an org-scoped access token
     */
    static async exchangeOrgConsoleSwitchToken(token: string): Promise<AdminOrgSwitchResponse> {
        try {
            return await apiClient.post<AdminOrgSwitchResponse>('/api/org/console-switch/exchange', {token});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get provider console configuration for an organization (admin action)
     */
    static async getOrganizationProviderConsole(orgId: number): Promise<OrganizationProviderConsoleResponse> {
        try {
            return await apiClient.get<OrganizationProviderConsoleResponse>(
                `/api/admin/organizations/${orgId}/provider-console`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update provider console configuration for an organization (admin action)
     */
    static async updateOrganizationProviderConsole(
        orgId: number,
        payload: OrganizationProviderConsoleUpdateRequest
    ): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(
                `/api/admin/organizations/${orgId}/provider-console`,
                payload
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Disable all provider console modules for an organization (admin action)
     */
    static async disableOrganizationProviderConsole(orgId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(
                `/api/admin/organizations/${orgId}/provider-console/disable`,
                {}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update organization subdomain (admin action)
     */
    static async updateOrganizationFeatures(
        orgId: number,
        features: Record<string, unknown>
    ): Promise<{success: boolean; features: Record<string, unknown>}> {
        return apiClient.put(`/api/admin/organizations/${orgId}/features`, {features});
    }

    static async updateOrganizationName(orgId: number, name: string): Promise<{success: boolean; name: string}> {
        return apiClient.put<{success: boolean; name: string}>(`/api/admin/organizations/${orgId}/name`, {name});
    }

    static async updateOrganizationSubdomain(
        orgId: number,
        subdomain: string
    ): Promise<{success: boolean; subdomain: string}> {
        try {
            return await apiClient.put<{success: boolean; subdomain: string}>(
                `/api/admin/organizations/${orgId}/subdomain`,
                {subdomain}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Move provider to organization (admin action)
     */
    static async moveProviderToOrganization(
        providerId: number,
        organizationId: number
    ): Promise<{success: boolean; provider_id: number; from_organization_id: number; to_organization_id: number}> {
        try {
            return await apiClient.post<{
                success: boolean;
                provider_id: number;
                from_organization_id: number;
                to_organization_id: number;
            }>(`/api/admin/providers/${providerId}/organization`, {organization_id: organizationId});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update provider display name (admin action)
     */
    static async updateProviderName(orgId: number, providerId: number, name: string): Promise<ProviderRenameResponse> {
        try {
            return await apiClient.put<ProviderRenameResponse>(
                `/api/admin/organizations/${orgId}/providers/${providerId}/name`,
                {name}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Add a new provider to an organization (admin action)
     */
    static async addProvider(orgId: number, name: string, npi?: string): Promise<AddProviderResponse> {
        try {
            const body: {name: string; npi?: string} = {name};
            if (npi) body.npi = npi;
            return await apiClient.post<AddProviderResponse>(`/api/admin/organizations/${orgId}/providers`, body);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete (deactivate) a provider and reassign its data to another provider (admin action)
     */
    static async deleteProvider(
        orgId: number,
        providerId: number,
        targetProviderId: number
    ): Promise<DeleteProviderResponse> {
        try {
            return await apiClient.post<DeleteProviderResponse>(
                `/api/admin/organizations/${orgId}/providers/${providerId}/delete`,
                {target_provider_id: targetProviderId}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Upload organization logo (admin action)
     */
    static async uploadOrganizationLogo(orgId: number, file: File): Promise<{success: boolean; logo_url: string}> {
        try {
            const formData = new FormData();
            formData.append('logo', file);
            return await apiClient.post<{success: boolean; logo_url: string}>(
                `/api/admin/organizations/${orgId}/logo`,
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                }
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update organization logo url (admin action)
     */
    static async updateOrganizationLogoUrl(
        orgId: number,
        logoUrl: string
    ): Promise<{success: boolean; logo_url: string}> {
        try {
            return await apiClient.put<{success: boolean; logo_url: string}>(`/api/admin/organizations/${orgId}/logo`, {
                logo_url: logoUrl,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete organization logo (admin action)
     */
    static async deleteOrganizationLogo(orgId: number): Promise<{success: boolean; logo_url: string | null}> {
        try {
            return await apiClient.delete<{success: boolean; logo_url: string | null}>(
                `/api/admin/organizations/${orgId}/logo`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update an organization's white-labeling custom CSS (super-admin only).
     * Pass an empty string to clear it back to the brand defaults.  See
     * STYLE_GUIDE §2 for the contract — the CSS body should override the
     * --mld-* variables in :root.
     */
    static async updateOrganizationCustomCss(
        orgId: number,
        customCss: string
    ): Promise<{success: boolean; custom_css: string | null}> {
        return apiClient.put<{success: boolean; custom_css: string | null}>(
            `/api/admin/organizations/${orgId}/custom-css`,
            {custom_css: customCss}
        );
    }

    /**
     * Create organization user (admin action)
     */
    static async createOrganizationUser(
        orgId: number,
        payload: {first_name: string; last_name: string; email: string; phone: string}
    ): Promise<{success: boolean; user_id?: number}> {
        try {
            return await apiClient.post<{success: boolean; user_id?: number}>(
                `/api/admin/organizations/${orgId}/users/org`,
                payload
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update organization user (admin action)
     */
    static async updateOrganizationUser(
        orgId: number,
        userId: number,
        payload: {first_name: string; last_name: string; email: string; phone: string}
    ): Promise<{success: boolean}> {
        try {
            return await apiClient.put<{success: boolean}>(
                `/api/admin/organizations/${orgId}/users/org/${userId}`,
                payload
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create provider user (admin action)
     */
    static async createProviderUser(
        orgId: number,
        providerId: number,
        payload: {first_name: string; last_name: string; email: string; phone: string; role_ids?: number[]}
    ): Promise<{success: boolean; user_id?: number}> {
        try {
            return await apiClient.post<{success: boolean; user_id?: number}>(
                `/api/admin/organizations/${orgId}/providers/${providerId}/users`,
                payload
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update provider user (admin action)
     */
    static async updateProviderUser(
        orgId: number,
        providerId: number,
        userId: number,
        payload: {first_name: string; last_name: string; email: string; phone: string; role_ids?: number[]}
    ): Promise<{success: boolean}> {
        try {
            return await apiClient.put<{success: boolean}>(
                `/api/admin/organizations/${orgId}/providers/${providerId}/users/${userId}`,
                payload
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete provider user (admin action)
     */
    static async deleteOrganizationUser(orgId: number, userId: number): Promise<{success: boolean}> {
        return apiClient.delete<{success: boolean}>(`/api/admin/organizations/${orgId}/users/org/${userId}`);
    }

    static async deleteProviderUser(orgId: number, providerId: number, userId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(
                `/api/admin/organizations/${orgId}/providers/${providerId}/users/${userId}`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Organization catalog (panels + profiles)
     */
    static async getOrganizationCatalog(orgId: number): Promise<OrganizationCatalogResponse> {
        try {
            return await apiClient.get<OrganizationCatalogResponse>(`/api/admin/organizations/${orgId}/catalog`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get detailed user information by ID
     */
    static async getUserById(userId: number): Promise<any> {
        try {
            return await apiClient.get<any>(`/api/admin/users/${userId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user information
     */
    static async updateUser(userInfo: any): Promise<any> {
        try {
            return await apiClient.post<any>('/api/user', userInfo);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user by ID (admin action — supports email changes)
     */
    static async updateUserById(userId: number, userData: Partial<UserSearchResult>): Promise<UserSearchResult> {
        try {
            return await apiClient.put<UserSearchResult>(`/api/admin/users/${userId}`, userData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send verification email to a user (admin action)
     */
    static async sendVerificationEmail(email: string): Promise<{success: boolean; token?: string}> {
        try {
            return await apiClient.post<{success: boolean; token?: string}>('/api/sendVerify', {
                username: email,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete user account
     */
    static async deleteUser(userId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/admin/users/${userId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Reset user password (admin action) - matches old format
     */
    static async resetUserPassword(email: string, newPassword: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/admin/reset', {
                username: email, // Old version uses 'username'
                password: newPassword,
                ref: 'admin', // Old version includes 'ref'
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Mail a password-reset link to a user (admin action).
     *
     * Prefer this over `resetUserPassword` from admin screens: it lets the user
     * choose their own password instead of requiring the admin to know one, and
     * unlike the public /api/reset flow it reports the real outcome.
     */
    static async sendPasswordResetEmail(email: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/admin/send-password-reset', {
                username: email,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Build the right query string for the recommendations endpoints.
     * Super-admin global authoring uses `?scope=global`; per-org views
     * use `?orgId=N`. Mutually exclusive — global wins.
     */
    private static recScopeQs(opts?: {orgId?: number; globalScope?: boolean}): string {
        if (opts?.globalScope) return '?scope=global';
        if (opts?.orgId) return `?orgId=${opts.orgId}`;
        return '';
    }

    /**
     * Get all system recommendations
     */
    static async getAllRecommendations(orgId?: number, opts?: {globalScope?: boolean}): Promise<Recommendation[]> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            return await apiClient.get<Recommendation[]>(`/api/recommendations${qs}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Export the global BioMatch library. The server emails the .sql snapshot
     * to the internal allowlist when SendGrid is configured; otherwise it
     * returns the SQL for the caller to download.
     */
    static async exportGlobalBioMatch(): Promise<BioMatchExportResult> {
        return apiClient.post<BioMatchExportResult>('/api/recommendations/export?scope=global', {});
    }

    /**
     * Get single recommendation by ID
     */
    static async getRecommendation(
        recommendationId: number,
        orgId?: number,
        opts?: {globalScope?: boolean}
    ): Promise<Recommendation> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            return await apiClient.get<Recommendation>(`/api/recommendations/${recommendationId}${qs}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create new recommendation
     */
    static async createRecommendation(
        recommendation: Omit<Recommendation, 'id'>,
        opts?: {orgId?: number; globalScope?: boolean}
    ): Promise<Recommendation> {
        try {
            const qs = AdminService.recScopeQs(opts);
            return await apiClient.post<Recommendation>(`/api/recommendations${qs}`, recommendation);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update existing recommendation
     */
    static async updateRecommendation(
        recommendationId: number,
        updates: Omit<Recommendation, 'id'>,
        orgId?: number,
        opts?: {globalScope?: boolean}
    ): Promise<any> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            const result = await apiClient.put<any>(`/api/recommendations/${recommendationId}${qs}`, updates);
            return result;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Status / billing change driven by the BioMatch row dropdown.
     * Server picks the right write path: globals upsert into the
     * per-org billing override; org-owned recs partial-update directly.
     */
    static async updateRecommendationBilling(
        recommendationId: number,
        body: {
            enabled?: boolean;
            billing_type: string;
            employer_coverage_percent: number | null;
        },
        orgId?: number,
        opts?: {globalScope?: boolean}
    ): Promise<void> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            await apiClient.put(`/api/recommendations/${recommendationId}/billing${qs}`, body);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete recommendation
     */
    static async deleteRecommendation(
        recommendationId: number,
        orgId?: number,
        opts?: {globalScope?: boolean}
    ): Promise<{success: boolean}> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            return await apiClient.delete<{success: boolean}>(`/api/recommendations/${recommendationId}${qs}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search orders
     */
    static async searchOrders(searchFields: any): Promise<any[]> {
        try {
            return await apiClient.post<any[]>('/api/order/search', searchFields);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get order details
     */
    static async getOrder(orderId: number): Promise<any> {
        try {
            return await apiClient.get<any>(`/api/admin/orders/${orderId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update order status
     */
    static async updateOrderStatus(orderId: number, status: string): Promise<{success: boolean}> {
        try {
            return await apiClient.patch<{success: boolean}>(`/api/admin/orders/${orderId}/status`, {status});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search photos/media - using direct axios to match old implementation
     */
    static async searchPhotos(email: string): Promise<any> {
        try {
            const axios = (await import('axios')).default;

            // Get token from localStorage like old implementation
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios(`${settings.apiURL}/api/claim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                data: JSON.stringify({email}),
            });

            if (response.status !== 200) {
                throw new Error('Could not get photos');
            }

            return response.data;
        } catch (error) {
            logError('Photo search error', error);
            throw error;
        }
    }

    /**
     * Search kit videos by kit ID (matches old VideoSearch)
     */
    static async searchKitVideos(kitId: string): Promise<any> {
        try {
            return await apiClient.post<any>('/api/kit/getKit', {kit_id: kitId});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get recommendations (alias for getAllRecommendations)
     */
    static async getRecommendations(): Promise<any[]> {
        try {
            const result = await this.getAllRecommendations();
            return result;
        } catch (error) {
            logError('Get recommendations error', error);
            throw error;
        }
    }

    /**
     * Add recommendation - now receives correct format from new component
     */
    static async addRecommendation(recommendation: any, orgId?: number, opts?: {globalScope?: boolean}): Promise<any> {
        try {
            const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
            const result = await apiClient.post<any>(`/api/recommendations${qs}`, recommendation);
            return result;
        } catch (error) {
            logError('Add recommendation error', error);
            throw error;
        }
    }

    /**
     * Upload media file
     */
    static async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<{url: string}> {
        try {
            return await apiClient.uploadFile<{url: string}>('/api/admin/media/upload', file, onProgress);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete media file
     */
    static async deleteMedia(mediaId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/admin/media/${mediaId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search videos
     */
    static async searchVideos(searchTerm: string): Promise<any[]> {
        try {
            return await apiClient.post<any[]>('/api/admin/videos/search', {search: searchTerm});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get rejected emails
     */
    static async getRejectedEmails(startDatetime?: number, endDatetime?: number): Promise<any[]> {
        try {
            const params = new URLSearchParams();
            if (startDatetime) params.append('startDatetime', startDatetime.toString());
            if (endDatetime) params.append('endDatetime', endDatetime.toString());

            const queryString = params.toString();
            const url = queryString ? `/api/email/rejected?${queryString}` : '/api/email/rejected';

            return await apiClient.get<any[]>(url);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Resend rejected email
     */
    static async resendRejectedEmail(emailId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(`/api/admin/emails/${emailId}/resend`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update test information
     */
    static async updateTest(testId: number, updates: any): Promise<any> {
        try {
            return await apiClient.patch<any>(`/api/admin/tests/${testId}`, updates);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get system statistics
     */
    static async getSystemStats(): Promise<any> {
        try {
            return await apiClient.get<any>('/api/admin/stats');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get audit logs
     */
    static async getAuditLogs(limit?: number, offset?: number): Promise<any[]> {
        try {
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit.toString());
            if (offset) params.append('offset', offset.toString());

            const queryString = params.toString();
            const url = queryString ? `/api/admin/audit-logs?${queryString}` : '/api/admin/audit-logs';

            return await apiClient.get<any[]>(url);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Export user data (admin action)
     */
    static async exportUserData(userId: number): Promise<Blob> {
        try {
            return await apiClient.get(`/api/admin/users/${userId}/export`, {
                responseType: 'blob',
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Bulk export system data
     */
    static async bulkExportData(dataType: string, filters?: any): Promise<Blob> {
        try {
            return await apiClient.post('/api/admin/export/bulk', {dataType, filters}, {responseType: 'blob'});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send system-wide notification
     */
    static async sendSystemNotification(notification: {
        title: string;
        message: string;
        userIds?: number[];
        sendToAll?: boolean;
    }): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/admin/notifications', notification);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search biomarkers by name with fuzzy matching
     */
    static async searchBiomarkers(searchTerm: string): Promise<BiomarkerSearchResult[]> {
        try {
            return await apiClient.get<BiomarkerSearchResult[]>(
                `/api/biomarkers/search?search=${encodeURIComponent(searchTerm)}`
            );
        } catch (error) {
            logError('Biomarker search error', error);
            throw error;
        }
    }

    /**
     * Get assay catalog for pricing calculator
     */
    // ── Kit Cost Settings (super-admin) ────────────────────────────

    static async getKitCostSettings(): Promise<{
        settings: KitCostSettings;
        tiers: KitPackageTier[];
    }> {
        return apiClient.get('/api/admin/kit-cost-settings');
    }

    static async updateKitCostSettings(body: Partial<KitCostSettings>): Promise<KitCostSettings> {
        return apiClient.put('/api/admin/kit-cost-settings', body);
    }

    static async updateKitPackageTiers(
        tiers: Array<{device_count: number; package_cost_cents: number; label: string | null}>
    ): Promise<{tiers: KitPackageTier[]}> {
        return apiClient.put('/api/admin/kit-cost-settings/tiers', {tiers});
    }

    static async getAssayCatalog(): Promise<AssayCatalogItem[]> {
        try {
            return await apiClient.get<AssayCatalogItem[]>('/api/admin/assay-catalog');
        } catch (error) {
            throw error;
        }
    }

    /**
     * List OHD campaigns for an MLD organization (proxied from OHD's
     * listOrganizations). Powers the curation modal's campaign picker.
     */
    static async getOhdCampaignsForOrg(orgId: number): Promise<{
        orgId: number;
        ohdOrgId: number | null;
        campaigns: Array<{id: number; name: string; startsAt: string; endsAt: string}>;
    }> {
        return apiClient.get(`/api/admin/ohd-campaigns?orgId=${orgId}`);
    }

    /**
     * OHD panel curation for a given campaign. Returns each curated
     * panel + its tests (from ohd_campaign_tests) + the assay catalog
     * for the picker. Single round trip.
     *
     * Passing `orgId` enables the BE's lazy-sync path — if we've never
     * seen this campaign before, the BE pulls test_list from OHD and
     * seeds the tables before responding.
     */
    static async getOhdCampaignPanels(
        campaignId: number,
        orgId?: number | null
    ): Promise<{
        campaignId: number;
        panels: Array<{
            id: number;
            campaignId: number;
            panel: string;
            assayCode: string | null;
            showAsCard: boolean;
            displayName: string | null;
            sortOrder: number;
            genderFilter: 'm' | 'f' | null;
            placeholder: boolean;
            loincOverrides: string[] | null;
            tests: Array<{
                id: number;
                name: string;
                loinc: string | null;
                occurrenceCount: number;
                lastSeenAt: string;
            }>;
        }>;
        unassignedTests: Array<{
            id: number;
            name: string;
            loinc: string | null;
            occurrenceCount: number;
            lastSeenAt: string;
        }>;
        assays: Array<{
            code: string;
            name: string;
            isBundledPanel: boolean;
            isCalculated: boolean;
            classification: number | null;
        }>;
    }> {
        const qs = new URLSearchParams({campaignId: String(campaignId)});
        if (orgId) qs.set('orgId', String(orgId));
        return apiClient.get(`/api/admin/ohd-campaign-panels?${qs.toString()}`);
    }

    /**
     * Super-admin: re-tag rows that a bad ELIS CSV import wrote under
     * the wrong (organization_id, provider_id). Dry-run (apply=false)
     * returns counts + sample; apply=true commits in a transaction.
     */
    static async backfillMisimportedOrgProvider(
        orgId: number,
        body: {correctProvider: number; apply: boolean}
    ): Promise<{
        correctOrg: number;
        correctProvider: number;
        sourceSubdomains: string[];
        sourceOrgIds: number[];
        ohdUsersFetched: number;
        ohdEmailsDistinct: number;
        localUsersMatched: number;
        affectedRows: {orders: number; results: number; user_providers: number; ohd_result_sets: number};
        sampleOrders: Array<{
            id: number;
            oid: string;
            user_id: number;
            first_name: string | null;
            last_name: string | null;
            created_date: string;
        }>;
        applied: boolean;
        updates?: {
            orders: number;
            results: number;
            ohdResultSets: number;
            userProvidersEnsured: number;
            userProvidersDeleted: number;
        };
    }> {
        return apiClient.post(`/api/admin/organizations/${orgId}/backfill-misimported-org-provider`, body);
    }

    static async updateOhdCampaignPanel(
        mappingId: number,
        body: Partial<{
            showAsCard: boolean;
            displayName: string | null;
            sortOrder: number;
            genderFilter: 'm' | 'f' | null;
            placeholder: boolean;
            assayCode: string | null;
            /** Comma/space-separated LOINC list; BE parses + validates. Null clears. */
            loincOverrides: string | null;
        }>
    ): Promise<{
        id: number;
        campaignId: number;
        panel: string;
        assayCode: string | null;
        showAsCard: boolean;
        displayName: string | null;
        sortOrder: number;
        genderFilter: 'm' | 'f' | null;
        placeholder: boolean;
        loincOverrides: string[] | null;
    }> {
        return apiClient.patch(`/api/admin/ohd-campaign-panels/${mappingId}`, body);
    }

    /**
     * Get organization partner tier
     */
    static async getOrganizationPartnerTier(orgId: number): Promise<{partner_tier: PartnerTier | null}> {
        try {
            return await apiClient.get<{partner_tier: PartnerTier | null}>(
                `/api/admin/organizations/${orgId}/partner-tier`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update organization partner tier
     */
    static async updateOrganizationPartnerTier(
        orgId: number,
        tier: PartnerTier | null
    ): Promise<{success: boolean; partner_tier: PartnerTier | null}> {
        try {
            return await apiClient.put<{success: boolean; partner_tier: PartnerTier | null}>(
                `/api/admin/organizations/${orgId}/partner-tier`,
                {partner_tier: tier}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get all pricing settings for an organization
     */
    static async getPricingSettings(orgId: number): Promise<PricingSettings> {
        try {
            return await apiClient.get<PricingSettings>(`/api/admin/organizations/${orgId}/pricing-settings`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update all pricing settings for an organization
     */
    static async updatePricingSettings(orgId: number, settings: PricingSettings): Promise<void> {
        try {
            await apiClient.put(`/api/admin/organizations/${orgId}/pricing-settings`, settings);
        } catch (error) {
            throw error;
        }
    }

    /**
     * List pricing panels for an organization
     */
    static async listPricingPanels(orgId: number): Promise<PricingPanel[]> {
        try {
            return await apiClient.get<PricingPanel[]>(`/api/admin/organizations/${orgId}/pricing-panels`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create a new pricing panel for an organization
     */
    static async createPricingPanel(orgId: number, name: string, testCodes: string[]): Promise<{panelId: number}> {
        try {
            return await apiClient.post<{panelId: number}>(`/api/admin/organizations/${orgId}/pricing-panels`, {
                name,
                test_codes: testCodes,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update an existing pricing panel
     */
    static async updatePricingPanel(
        orgId: number,
        panelId: number,
        name: string,
        testCodes: string[]
    ): Promise<{success: boolean}> {
        try {
            return await apiClient.put<{success: boolean}>(
                `/api/admin/organizations/${orgId}/pricing-panels/${panelId}`,
                {name, test_codes: testCodes}
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create a new profile for an organization
     */
    static async createProfile(orgId: number, name: string, panelIds: number[]): Promise<{profileId: number}> {
        try {
            return await apiClient.post<{profileId: number}>(`/api/admin/organizations/${orgId}/profiles`, {
                name,
                panel_ids: panelIds,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update panels for an existing profile
     */
    static async updateProfilePanels(profileId: number, panelIds: number[]): Promise<{success: boolean}> {
        try {
            return await apiClient.put<{success: boolean}>(`/api/admin/profiles/${profileId}/panels`, {
                panel_ids: panelIds,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete a pricing panel from an organization
     */
    static async deletePricingPanel(orgId: number, panelId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(
                `/api/admin/organizations/${orgId}/pricing-panels/${panelId}`
            );
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete a profile from an organization
     */
    static async deleteProfile(orgId: number, profileId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(
                `/api/admin/organizations/${orgId}/profiles/${profileId}`
            );
        } catch (error) {
            throw error;
        }
    }

    // ── Reference Range Overrides ──────────────────────────────────────

    static async listReferenceRanges(orgId: number): Promise<OrgReferenceRange[]> {
        const res = await apiClient.get<{ranges: OrgReferenceRange[]}>(
            `/api/admin/organizations/${orgId}/reference-ranges`
        );
        return res.ranges;
    }

    static async createReferenceRange(orgId: number, input: OrgReferenceRangeInput): Promise<OrgReferenceRange> {
        return apiClient.post<OrgReferenceRange>(`/api/admin/organizations/${orgId}/reference-ranges`, input);
    }

    static async updateReferenceRange(
        orgId: number,
        id: number,
        input: Partial<OrgReferenceRangeInput>
    ): Promise<OrgReferenceRange> {
        return apiClient.put<OrgReferenceRange>(`/api/admin/organizations/${orgId}/reference-ranges/${id}`, input);
    }

    static async deleteReferenceRange(orgId: number, id: number): Promise<void> {
        await apiClient.delete(`/api/admin/organizations/${orgId}/reference-ranges/${id}`);
    }

    // ── Storefront Products ────────────────────────────────────────────

    static async listStorefrontProducts(orgId: number): Promise<StorefrontProduct[]> {
        const res = await apiClient.get<{products: StorefrontProduct[]}>(
            `/api/admin/organizations/${orgId}/storefront-products`
        );
        return res.products;
    }

    static async createStorefrontProduct(
        orgId: number | null,
        input: StorefrontProductInput
    ): Promise<StorefrontProduct> {
        const url =
            orgId == null ? '/api/admin/storefront-products' : `/api/admin/organizations/${orgId}/storefront-products`;
        return apiClient.post<StorefrontProduct>(url, input);
    }

    static async updateStorefrontProduct(
        orgId: number | null,
        productId: number,
        input: Partial<StorefrontProductInput>
    ): Promise<StorefrontProduct> {
        const url =
            orgId == null
                ? `/api/admin/storefront-products/${productId}`
                : `/api/admin/organizations/${orgId}/storefront-products/${productId}`;
        return apiClient.put<StorefrontProduct>(url, input);
    }

    static async deleteStorefrontProduct(orgId: number | null, productId: number): Promise<void> {
        const url =
            orgId == null
                ? `/api/admin/storefront-products/${productId}`
                : `/api/admin/organizations/${orgId}/storefront-products/${productId}`;
        await apiClient.delete(url);
    }

    /**
     * Per-org overlay for a storefront product: enable/disable + price
     * overrides. Used to deactivate a global from this org's catalog
     * or mark up / mark down its price.
     */
    static async upsertStorefrontProductOrgState(
        orgId: number,
        productId: number,
        body: {
            enabled?: boolean;
            price_override_cents?: number | null;
            member_price_override_cents?: number | null;
            billing_type?: string;
            employer_coverage_percent?: number | null;
        }
    ): Promise<{org_state: import('@/shared/types/api').StorefrontProductOrgState}> {
        return apiClient.put(`/api/admin/organizations/${orgId}/storefront-products/${productId}/org-state`, body);
    }

    /**
     * Super-admin global products list. Same shape as
     * listStorefrontProducts, but scoped to organization_id IS NULL.
     */
    static async listGlobalStorefrontProducts(): Promise<StorefrontProduct[]> {
        const res = await apiClient.get<{products: StorefrontProduct[]}>('/api/admin/storefront-products/global');
        return res.products;
    }

    /**
     * Export the admin-built global storefront products. The server emails the
     * .sql snapshot to the internal allowlist when SendGrid is configured;
     * otherwise it returns the SQL for the caller to download. Mirrors
     * exportGlobalBioMatch.
     */
    static async exportGlobalStorefrontProducts(): Promise<GlobalProductsExportResult> {
        return apiClient.post<GlobalProductsExportResult>('/api/admin/storefront-products/export', {});
    }

    static async uploadStorefrontProductImage(
        orgId: number | null,
        productId: number,
        file: File
    ): Promise<{success: boolean; image_url: string}> {
        const formData = new FormData();
        formData.append('image', file);
        const url =
            orgId == null
                ? `/api/admin/storefront-products/${productId}/image`
                : `/api/admin/organizations/${orgId}/storefront-products/${productId}/image`;
        return apiClient.post(url, formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        });
    }

    static async uploadRecommendationImage(
        recommendationId: number,
        file: File,
        orgId?: number,
        opts?: {globalScope?: boolean}
    ): Promise<{success: boolean; image_url: string}> {
        const formData = new FormData();
        formData.append('image', file);
        const qs = AdminService.recScopeQs({orgId, globalScope: opts?.globalScope});
        return apiClient.post(`/api/recommendations/${recommendationId}/image${qs}`, formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        });
    }

    // ── Provider API Keys ────────────────────────────────────────────────

    static async listProviderApiKeys(providerId: number, organizationId: number): Promise<ProviderApiKey[]> {
        const res = await apiClient.get<{keys: ProviderApiKey[]}>(
            `/api/admin/providers/${providerId}/api-keys?organizationId=${organizationId}`
        );
        return res.keys;
    }

    static async createProviderApiKey(
        providerId: number,
        input: {label?: string; expiresAt?: string; organizationId: number}
    ): Promise<ProviderApiKeyCreateResponse> {
        return apiClient.post<ProviderApiKeyCreateResponse>(`/api/admin/providers/${providerId}/api-keys`, input);
    }

    static async revokeProviderApiKey(providerId: number, keyId: number): Promise<void> {
        await apiClient.delete(`/api/admin/providers/${providerId}/api-keys/${keyId}`);
    }

    // ── Coupons ────────────────────────────────────────────────────────

    static async listCoupons(params: {
        organizationId?: number;
        search?: string;
        valid?: boolean;
        limit?: number;
        offset?: number;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
    }): Promise<AdminCouponListResponse> {
        const query = new URLSearchParams();
        if (params.organizationId != null) query.set('organizationId', String(params.organizationId));
        if (params.search) query.set('search', params.search);
        if (params.valid != null) query.set('valid', String(params.valid));
        if (params.limit != null) query.set('limit', String(params.limit));
        if (params.offset != null) query.set('offset', String(params.offset));
        if (params.sortBy) query.set('sortBy', params.sortBy);
        if (params.sortDir) query.set('sortDir', params.sortDir);
        return apiClient.get<AdminCouponListResponse>(`/api/admin/coupons?${query.toString()}`);
    }

    static async createCoupon(input: AdminCouponInput): Promise<AdminCoupon> {
        return apiClient.post<AdminCoupon>('/api/admin/coupons', input);
    }

    static async updateCoupon(id: number, input: Partial<AdminCouponInput>): Promise<AdminCoupon> {
        return apiClient.put<AdminCoupon>(`/api/admin/coupons/${id}`, input);
    }

    static async invalidateCoupon(id: number): Promise<void> {
        await apiClient.post(`/api/admin/coupons/${id}/invalidate`, {});
    }
}

export default AdminService;
